/**
 * Inteligencia por CUIT — lambda `cuit_intel` de webadmin2 (JWT post-MFA).
 *
 * Índice propio de received_payments por CUIT pagador y collector: búsqueda
 * simétrica (pagador o cobrador), grafo de profundidad 2, fichas, muestras,
 * búsqueda por monto/rango e id de Coelsa. Las lecturas son sobre la tabla
 * propia (ms); sólo el id de Coelsa va en vivo al core.
 */
import { authFetch, authHeader, jsonOrThrow, qsFrom } from "./monitoring";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:3000";

export type QueryTipo = "cuit" | "rfc" | "collector" | "coelsa_id" | "amount" | "amount_range" | null;

export interface Bucket { i: number; label: string; n: number; sum: number | null }

export interface PayerNode {
  cuit:         string;
  dv_ok:        boolean;
  n:            number;
  sum:          number;
  nr:           number;
  sr:           number;
  n_collectors: number;
  is_hub:       boolean;
  first_ts:     string | null;
  last_ts:      string | null;
  first_month:  string | null;
  last_month:   string | null;
  name:         string | null;
  names:        Record<string, number>;
  cbus:         Record<string, number>;
  banks:        Record<string, number>;
  months:       Record<string, [number, number]>;
  collectors:   Record<string, [number, number]>;
  buckets:      Bucket[];
  updated_at:   string | null;
}

export interface CollectorInfo {
  id:             string;
  nombre:         string | null;
  razon_social:   string | null;
  tax_id:         string | null;
  activo:         boolean | null;
  processor:      string | null;
  sub_processor:  string | null;
  tipo:           string | null;
  n:              number;
  sum:            number;
  first_month:    string | null;
  last_month:     string | null;
}

export interface Padron {
  razon_social?:           string;
  condicion_iva_desc?:     string;
  direccion?:              string;
  localidad?:              string;
  cod_postal?:             string;
  provincia?:              string;
  consultado_at?:          string;
  actividad_principal?:    string;
  actividades?:            { id: string; descripcion: string; orden: number; periodo: string }[];
  error?:                  string;
}

export interface Payment {
  id:                  string;
  ts:                  string | null;
  date:                string | null;
  amount:              number | null;
  status:              string | null;
  cuit:                string | null;
  name:                string | null;
  cbu:                 string | null;
  bank:                string | null;
  collector:           string | null;
  bdate:               string | null;
  customer_id:         string | null;
  account_description: string | null;
}

export interface SearchResult {
  query:       string;
  tipo:        QueryTipo;
  cuit?:       string;
  valido?:     boolean;
  error?:      string;
  min?:        number;
  max?:        number;
  id?:         string;
  payer:       PayerNode | null;
  collectors:  CollectorInfo[];
  padron:      Padron | null;
  payment?:    Payment | null;
  found?:      boolean;
  message?:    string;
}

export interface SeriesPoint { month: string; n: number; sum: number; nr?: number; payers?: number | null }

export interface PayerEdge {
  collector:      string;
  n:              number;
  sum:            number;
  nr:             number;
  sr:             number;
  months:         Record<string, [number, number, number]>;
  buckets:        Bucket[];
  names:          Record<string, number>;
  cbus:           Record<string, number>;
  banks:          Record<string, number>;
  first_ts:       string;
  last_ts:        string;
  collector_info: CollectorInfo;
}

export interface PayerDetail {
  node:         PayerNode;
  from:         string;
  to:           string;
  series:       SeriesPoint[];
  edges:        PayerEdge[];
  collector_of: CollectorInfo[];
}

export interface TopPayer { cuit: string; name: string; n: number; sum: number; is_hub: boolean }

export interface CollectorMonth {
  month:        string;
  n:            number;
  sum:          number;
  nr:           number;
  sr:           number;
  payers:       number;
  unknown_n:    number;
  avg:          number;
  top10_share:  number;
  buckets:      Bucket[];
  payer_freq:   Record<string, number>;
  top_sum:      TopPayer[];
  top_n:        TopPayer[];
  computed_at:  string | null;
}

export interface CollectorDetail {
  node:             CollectorInfo;
  series:           SeriesPoint[];
  month:            CollectorMonth | null;
  available_months: string[];
}

export interface Sample { id: string; ts: string; amt: number; cbu: string; st: string }

export interface EdgeMonth {
  month: string; n: number; sum: number; nr: number; sr: number; min: number; max: number; avg: number;
  active_days: number; n_round: number; buckets: Bucket[];
  names: Record<string, number>; cbus: Record<string, number>; banks: Record<string, number>;
}

export interface EdgeDetail {
  cuit:      string;
  collector: CollectorInfo;
  from:      string;
  to:        string;
  months:    EdgeMonth[];
  days:      { date: string; n: number; sum: number }[];
  samples:   Sample[];
}

export interface GraphNode {
  id:            string;              // "payer:<cuit>" | "collector:<id>"
  type:          "payer" | "collector";
  label:         string;
  cuit?:         string;
  collector?:    string;
  n:             number;
  sum:           number;
  is_hub?:       boolean;
  n_collectors?: number;
  dv_ok?:        boolean;
  tax_id?:       string | null;
  processor?:    string | null;
  activo?:       boolean | null;
  first_ts?:     string | null;
  last_ts?:      string | null;
}

export interface GraphEdge {
  source:     string;
  target:     string;
  n?:         number;
  sum?:       number;
  nr?:        number;
  same_cuit?: boolean;
}

export interface Graph {
  root:      string;
  from:      string;
  to:        string;
  nodes:     GraphNode[];
  edges:     GraphEdge[];
  truncated: boolean;
}

export interface PaymentRow {
  id:        string;
  ts:        string;
  amount:    number;
  status:    string;
  cuit:      string;
  name:      string;
  collector: string;
  cbu:       string;
  month:     string;
}

export interface PaymentsResult {
  items:     PaymentRow[];
  months:    string[];
  min:       number;
  max:       number;
  collector: string | null;
  cuit:      string | null;
  cursor:    string | null;
  truncated: boolean;
}

export interface IntelStatus {
  first_date:          string | null;
  last_date:           string | null;
  retention_floor:     Record<string, string> | null;
  retention_probed_at: string | null;
  last_tick_at:        string | null;
  backfill_cursor:     string | null;
  backfill_floor:      Record<string, string> | null;
  today:               string;
  task_running:        string | null;
  buckets:             string[];
  acquirers:           string[];
}

export interface MonthRange { from?: string; to?: string }

async function get<T>(path: string, params: Record<string, string | undefined>, label: string): Promise<T> {
  const res = await authFetch(`${API_BASE}/cuit-intel/${path}${qsFrom(params)}`, { headers: authHeader() });
  return jsonOrThrow(res, label);
}

export function searchIntel(q: string): Promise<SearchResult> {
  return get("search", { q }, "search");
}

export function getStatus(): Promise<IntelStatus> {
  return get("status", {}, "status");
}

export function getPayer(cuit: string, r: MonthRange = {}): Promise<PayerDetail> {
  return get(`payer/${encodeURIComponent(cuit)}`, { from: r.from, to: r.to }, "payer");
}

export function getCollector(id: string, month?: string): Promise<CollectorDetail> {
  return get(`collector/${encodeURIComponent(id)}`, { month }, "collector");
}

export function getEdge(cuit: string, collector: string, r: MonthRange = {}): Promise<EdgeDetail> {
  return get(`edge/${encodeURIComponent(cuit)}/${encodeURIComponent(collector)}`, { from: r.from, to: r.to }, "edge");
}

export function getGraph(root: string, r: MonthRange = {}, k = 15): Promise<Graph> {
  return get("graph", { root, from: r.from, to: r.to, k: String(k) }, "graph");
}

export function getPayments(p: {
  min: number; max: number; months?: string[]; collector?: string; cuit?: string; cursor?: string | null; limit?: number;
}): Promise<PaymentsResult> {
  return get("payments", {
    min: String(p.min), max: String(p.max), months: p.months?.join(","),
    collector: p.collector, cuit: p.cuit, cursor: p.cursor ?? undefined,
    limit: p.limit ? String(p.limit) : undefined,
  }, "payments");
}

/** Consulta ARCA en vivo y persiste el padrón (endpoint de Facturación, misma lambda billing). */
export async function arcaLookup(cuit: string): Promise<Padron & { cuit: string }> {
  const res = await authFetch(`${API_BASE}/billing/receptors/${encodeURIComponent(cuit)}/arca-lookup`, {
    method: "POST", headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return jsonOrThrow(res, "arca-lookup");
}
